import { Api, UserRole } from "@/api";
import { create } from "zustand";
import { useCustomFurnitureStore } from "@/shared/catalog/sections/custom/store";
import { LocalStorageUtils } from "@/shared/lib/storage";

const refreshThresholdMs = 60_000;
let refreshTimer: number | undefined;

type AuthState = {
	accessToken: string | null;
	isAuthenticated: boolean;
	refreshToken: string | null;
	role: UserRole | null;
};

type TokenPair = {
	accessToken: string;
	refreshToken: string;
};

export const useAuthStore = create<AuthState>(() => ({
	accessToken: null,
	isAuthenticated: false,
	refreshToken: null,
	role: null,
}));

const roleClaim = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
const userIdClaim = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";

export const authUserId = (accessToken: string | null): string | null => {
	if (accessToken === null) return null;
	try {
		const payload = accessToken.split(".")[1];
		if (payload === undefined) return null;
		const claims = JSON.parse(atob(payload.replaceAll("-", "+").replaceAll("_", "/"))) as Record<
			string,
			unknown
		>;
		const id = claims[userIdClaim] ?? claims.nameid ?? claims.sub;
		return typeof id === "string" && id.length > 0 ? id : null;
	} catch {
		return null;
	}
};

const accessTokenExpiresAt = (accessToken: string): number | null => {
	try {
		const payload = accessToken.split(".")[1];
		if (payload === undefined) return null;
		const decoded = JSON.parse(atob(payload.replaceAll("-", "+").replaceAll("_", "/"))) as {
			exp?: unknown;
		};
		return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
	} catch {
		return null;
	}
};

const accessTokenRole = (accessToken: string): AuthState["role"] => {
	try {
		const payload = accessToken.split(".")[1];
		if (payload === undefined) return null;
		const decoded = JSON.parse(atob(payload.replaceAll("-", "+").replaceAll("_", "/"))) as Record<
			string,
			unknown
		>;
		const role = Number(decoded[roleClaim]);
		return role === UserRole.User || role === UserRole.Admin ? role : null;
	} catch {
		return null;
	}
};

const clearRefreshTimer = () => {
	if (refreshTimer === undefined) return;
	window.clearTimeout(refreshTimer);
	refreshTimer = undefined;
};

const scheduleRefresh = (accessToken: string) => {
	clearRefreshTimer();
	const expiresAt = accessTokenExpiresAt(accessToken);
	if (expiresAt === null) return;

	refreshTimer = window.setTimeout(
		() => void refreshAccessToken(),
		Math.max(0, expiresAt - Date.now() - refreshThresholdMs),
	);
};

const saveTokenPair = ({ accessToken, refreshToken }: TokenPair) => {
	LocalStorageUtils.setAuthData({ accessToken, refreshToken });
	useAuthStore.setState({
		accessToken,
		isAuthenticated: true,
		refreshToken,
		role: accessTokenRole(accessToken),
	});
	scheduleRefresh(accessToken);
};

export const saveAuthTokens = (accessToken: string, refreshToken: string): void => {
	saveTokenPair({ accessToken, refreshToken });
	void useCustomFurnitureStore.getState().loadCustomFurniture();
};

export const hasAccessToken = (): boolean => useAuthStore.getState().isAuthenticated;

export const refreshAccessToken = async (): Promise<boolean> => {
	const refreshToken = useAuthStore.getState().refreshToken;
	if (refreshToken === null) {
		clearAccessToken();
		return false;
	}

	const response = await Api.Auth.postApiAuthRefresh({ refreshToken });
	if (response.error !== null || response.data === null) {
		clearAccessToken();
		return false;
	}

	saveTokenPair(response.data);
	return true;
};

export const restoreAuthSession = async (): Promise<void> => {
	const authData = LocalStorageUtils.getAuthData();
	if (authData === null) {
		clearAccessToken();
		return;
	}
	const { accessToken, refreshToken } = authData;

	useAuthStore.setState({
		accessToken,
		isAuthenticated: true,
		refreshToken,
		role: accessTokenRole(accessToken),
	});
	const expiresAt = accessTokenExpiresAt(accessToken);
	if (expiresAt === null || expiresAt - Date.now() <= refreshThresholdMs) {
		const refreshed = await refreshAccessToken();
		if (refreshed) {
			await useCustomFurnitureStore.getState().loadCustomFurniture();
		}
		return;
	}

	scheduleRefresh(accessToken);
	await useCustomFurnitureStore.getState().loadCustomFurniture();
};

export const clearAccessToken = (): void => {
	clearRefreshTimer();
	LocalStorageUtils.clearAuthData();
	useCustomFurnitureStore.getState().clearCustomFurniture();
	useAuthStore.setState({
		accessToken: null,
		isAuthenticated: false,
		refreshToken: null,
		role: null,
	});
};
